import { Filter } from '../../../common/classes/Filter/Filter';
import { AccessLevel } from '../../../common/enums';
import { Column } from '../../../common/interfaces';
import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import plusGray from '../../../assets/images/icons/plus-gray.svg';
import { AutomationIds } from '../../../common/enums/AutomationElements.enum';
import { SymbolLookup } from '../../../common/utils/ViewSource';
import Button from '../../../components/Buttons/Button/Button';
import { useLanguageElements } from '../../../language-elements/withLanguageElementsHOC';
import FilterConditions from './FilterConditions';
import FilterFooter from './FilterFooter';
import ModalWrapper from '../../../components/Modal';
import { FilterActionType, FilterUI } from './types';
import FilterNameInput from './FilterNameInput';

interface Props {
    onDeleteFilter: (filterId: string) => Promise<boolean>;
    onCancelFilter: () => void;
    onApplyFilter: (filter: FilterUI) => Promise<boolean>;
    filter?: FilterUI;
    fields: Column[];
    picklistSymbolImageMap: Map<string, SymbolLookup | undefined>;
    currentUserAccessLevel: AccessLevel;
    filterAction?: FilterActionType;
    showModal?: boolean;
    onModalClose?: () => void;
}

const FilterWrapper: React.FC<Props> = (props) => {
    const {
        fields,
        filter: initialFilter,
        onApplyFilter,
        onDeleteFilter,
        onCancelFilter,
        currentUserAccessLevel,
        filterAction = FilterActionType.NEW_FILTER,
        showModal = false,
        onModalClose,
    } = props;

    const [activeCondition, setActiveCondition] = useState<number>(-1);
    const [filter, setFilter] = useState<FilterUI>(initialFilter ?? new Filter(fields).toDisplayModel());
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(showModal);
    const languageElements = useLanguageElements();

    useEffect(() => {
        setIsModalOpen(showModal);
    }, [showModal]);

    useEffect(() => {
        if (initialFilter || filterAction === FilterActionType.NEW_FILTER) {
            setFilter(initialFilter ?? new Filter(fields).toDisplayModel());
            setActiveCondition(-1);
            setShowErrors(false);
        }
    }, [initialFilter, filterAction, fields]);

    const handleModalClose = useCallback((): void => {
        setIsModalOpen(false);
        if (onModalClose) {
            onModalClose();
        }
    }, [onModalClose]);

    const handleCancelFilter = useCallback((): void => {
        // Reset filter state to initial values when canceling
        setFilter(initialFilter ?? new Filter(fields).toDisplayModel());
        setActiveCondition(-1);
        setShowErrors(false);
        setIsModalOpen(false);
        onCancelFilter();
    }, [initialFilter, fields, onCancelFilter]);

    const handleClickAddCondition = useCallback((): void => {
        const updatedFilter = new Filter(fields, filter, false);
        updatedFilter.addNewCondition();
        const newActiveCondition = filter.conditions.length;
        setActiveCondition(newActiveCondition);
        setFilter(updatedFilter.toDisplayModel());
        setShowErrors(false);
    }, [fields, filter]);

    const isFilterEmpty: boolean = filter.isFilterEmpty;

    const renderFilterContent = () => (
        <div className="filter-wrapper-container">
            <div className="container-header">
                <span>{languageElements.VIEW_FILTER}</span>
            </div>
            <div id={'filter-scroll-container'} className="conditions-wrapper">
                <FilterNameInput
                    filter={filter}
                    currentUserAccessLevel={currentUserAccessLevel}
                    fields={fields}
                    onFilterUpdate={setFilter}
                    onSetActiveCondition={setActiveCondition}
                    onSetShowErrors={setShowErrors}
                />
                <FilterConditions
                    conditions={filter.conditions}
                    fields={fields}
                    showErrors={showErrors}
                    isFilterEmpty={isFilterEmpty}
                    activeCondition={activeCondition}
                    disableDeleteCondition={filter.disableDeleteCondition}
                    languageElements={languageElements}
                    filter={filter}
                    onFilterUpdate={setFilter}
                    onActiveConditionUpdate={setActiveCondition}
                    onShowErrorsUpdate={setShowErrors}
                />
                <Button
                    className={'btn-add-condition'}
                    tabIndex={0}
                    onClick={handleClickAddCondition}
                    dataClientId={AutomationIds.FILTER_ADD_CONDITION_BUTTON}
                    disabled={filter.disableAddCondition}
                    icon={plusGray}
                    imageAltText={'plus icon'}
                    text={languageElements.FILTER_CONDITION}
                />
            </div>
            <FilterFooter
                filter={filter}
                fields={fields}
                isFilterEmpty={isFilterEmpty}
                showErrors={showErrors}
                setShowErrors={setShowErrors}
                setIsModalOpen={setIsModalOpen}
                setActiveCondition={setActiveCondition}
                onApplyFilter={onApplyFilter}
                onDeleteFilter={onDeleteFilter}
                onCancelFilter={handleCancelFilter}
                onModalClose={onModalClose}
            />
        </div>
    );

    return isModalOpen ? (
        <ModalWrapper focusTrap={false} isModalOpen={isModalOpen} onClose={handleModalClose} classes={'filter-wrapper'}>
            {renderFilterContent()}
        </ModalWrapper>
    ) : null;
};

export default FilterWrapper;
